import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import * as monaco from 'monaco-editor'
import { SelectionFeedbackInput } from './SelectionFeedbackInput'
import { initializeMonaco, getMonacoOptions } from '~/components/editors/monaco/monacoInitializer'
import type { BaseSelectionContext } from '~/types/canvas'

interface CanvasCodeEditorProps {
  initialContent: string
  onContentChange: (content: string) => void
  language?: string
  readOnly?: boolean
  placeholder?: string
  onSelectionFeedback?: (feedback: string, context: BaseSelectionContext) => void
  isProcessing?: boolean
  actionSlot?: React.ReactNode
}

const SELECTION_CONTEXT_SIZE = 50
const FEEDBACK_WIDGET_ID = 'canvas-code-feedback-widget'
const PROCESSING_HIGHLIGHT_CLASS = 'canvas-code-processing-highlight'

function toMonacoLanguage(language?: string): string {
  if (!language) return 'plaintext'
  return language
}

export function CanvasCodeEditor({
  initialContent,
  onContentChange,
  language = 'plaintext',
  readOnly = false,
  placeholder = 'Start writing...',
  onSelectionFeedback,
  isProcessing = false,
  actionSlot
}: CanvasCodeEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
  const modelRef = useRef<monaco.editor.ITextModel | null>(null)
  const isApplyingExternalContentRef = useRef(false)
  const activeSelectionRef = useRef<monaco.Selection | null>(null)
  const pendingSelectionRef = useRef<monaco.Selection | null>(null)
  const onContentChangeRef = useRef(onContentChange)
  const contentWidgetRef = useRef<monaco.editor.IContentWidget | null>(null)
  const widgetPositionRef = useRef<monaco.IPosition | null>(null)
  const widgetVisibleRef = useRef(false)
  const decorationCollectionRef = useRef<monaco.editor.IEditorDecorationsCollection | null>(null)
  const wasProcessingRef = useRef(isProcessing)
  const isMouseSelectingRef = useRef(false)

  const [isMonacoReady, setIsMonacoReady] = useState(false)
  const [monacoError, setMonacoError] = useState<string | null>(null)
  const [isFeedbackMode, setIsFeedbackMode] = useState(false)
  const [isWidgetVisible, setIsWidgetVisible] = useState(false)
  const [widgetDomNode, setWidgetDomNode] = useState<HTMLDivElement | null>(null)

  const monacoLanguage = useMemo(() => toMonacoLanguage(language), [language])
  const isReadOnly = readOnly || isProcessing
  const canShowToolbar = !isReadOnly && !!onSelectionFeedback
  const canShowToolbarRef = useRef(canShowToolbar)
  const isFeedbackModeRef = useRef(isFeedbackMode)
  const isProcessingRef = useRef(isProcessing)
  isProcessingRef.current = isProcessing

  useEffect(() => {
    onContentChangeRef.current = onContentChange
  }, [onContentChange])

  useEffect(() => {
    canShowToolbarRef.current = canShowToolbar
  }, [canShowToolbar])

  useEffect(() => {
    isFeedbackModeRef.current = isFeedbackMode
  }, [isFeedbackMode])

  const layoutFeedbackWidget = useCallback(() => {
    const editor = editorRef.current
    const widget = contentWidgetRef.current
    if (!editor || !widget) return
    editor.layoutContentWidget(widget)
  }, [])

  const setWidgetVisible = useCallback(
    (visible: boolean) => {
      widgetVisibleRef.current = visible
      setIsWidgetVisible(visible)
      layoutFeedbackWidget()
    },
    [layoutFeedbackWidget]
  )

  const clearProcessingHighlight = useCallback(() => {
    decorationCollectionRef.current?.set([])
  }, [])

  const applyProcessingHighlight = useCallback(
    (selection: monaco.Selection | null) => {
      if (!selection || selection.isEmpty()) {
        clearProcessingHighlight()
        return
      }

      const range = new monaco.Range(
        selection.startLineNumber,
        selection.startColumn,
        selection.endLineNumber,
        selection.endColumn
      )
      decorationCollectionRef.current?.set([
        {
          range,
          options: {
            inlineClassName: PROCESSING_HIGHLIGHT_CLASS
          }
        }
      ])
    },
    [clearProcessingHighlight]
  )

  const showWidgetForSelection = useCallback(
    (selection: monaco.Selection | null) => {
      if (!selection || selection.isEmpty() || !canShowToolbarRef.current) {
        widgetPositionRef.current = null
        setWidgetVisible(false)
        return
      }

      const anchorPosition =
        typeof selection.getSelectionStart === 'function'
          ? selection.getSelectionStart()
          : {
              lineNumber: selection.selectionStartLineNumber ?? selection.startLineNumber,
              column: selection.selectionStartColumn ?? selection.startColumn
            }

      widgetPositionRef.current = {
        lineNumber: anchorPosition.lineNumber,
        column: anchorPosition.column
      }
      setWidgetVisible(true)
    },
    [setWidgetVisible]
  )

  const clearFeedbackState = useCallback(
    (clearPendingSelection = true) => {
      setIsFeedbackMode(false)
      setWidgetVisible(false)
      if (clearPendingSelection) {
        pendingSelectionRef.current = null
      }
    },
    [setWidgetVisible]
  )

  const getSelectionContext = useCallback(
    (selection: monaco.Selection): BaseSelectionContext | null => {
      const model = modelRef.current
      if (!model || selection.isEmpty()) return null

      const selectedText = model.getValueInRange(selection)
      if (!selectedText.trim()) return null

      const fullText = model.getValue()
      const startOffset = model.getOffsetAt(selection.getStartPosition())
      const endOffset = model.getOffsetAt(selection.getEndPosition())

      return {
        selectedText,
        contextBefore: fullText.slice(
          Math.max(0, startOffset - SELECTION_CONTEXT_SIZE),
          startOffset
        ),
        contextAfter: fullText.slice(
          endOffset,
          Math.min(fullText.length, endOffset + SELECTION_CONTEXT_SIZE)
        ),
        startLine: selection.startLineNumber,
        endLine: selection.endLineNumber
      }
    },
    []
  )

  useEffect(() => {
    let mounted = true

    const init = async () => {
      try {
        await initializeMonaco('#ffffff')
        if (mounted) setIsMonacoReady(true)
      } catch (error) {
        if (mounted) {
          setMonacoError(error instanceof Error ? error.message : 'Failed to initialize editor')
        }
      }
    }

    init()
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!isMonacoReady || !containerRef.current || editorRef.current) return

    const model = monaco.editor.createModel(initialContent, monacoLanguage)
    modelRef.current = model

    const editor = monaco.editor.create(containerRef.current, {
      ...getMonacoOptions(isReadOnly),
      model,
      language: monacoLanguage,
      theme: 'github-light',
      automaticLayout: true,
      lineNumbers: 'on',
      glyphMargin: false,
      folding: false,
      scrollBeyondLastLine: false,
      renderLineHighlight: 'line',
      wordWrap: 'off',
      scrollbar: {
        vertical: 'auto',
        horizontal: 'auto'
      }
    })
    editorRef.current = editor
    decorationCollectionRef.current = editor.createDecorationsCollection()

    const domNode = document.createElement('div')
    domNode.className = 'z-20'
    setWidgetDomNode(domNode)

    const feedbackWidget: monaco.editor.IContentWidget = {
      getId: () => FEEDBACK_WIDGET_ID,
      getDomNode: () => domNode,
      getPosition: () => {
        if (!widgetVisibleRef.current || !widgetPositionRef.current) return null
        return {
          position: widgetPositionRef.current,
          preference: [
            monaco.editor.ContentWidgetPositionPreference.ABOVE,
            monaco.editor.ContentWidgetPositionPreference.BELOW
          ]
        }
      },
      suppressMouseDown: true
    }
    contentWidgetRef.current = feedbackWidget
    editor.addContentWidget(feedbackWidget)

    const contentDisposable = model.onDidChangeContent(() => {
      if (isApplyingExternalContentRef.current) return
      onContentChangeRef.current(model.getValue())
    })

    const selectionDisposable = editor.onDidChangeCursorSelection(event => {
      if (isFeedbackModeRef.current) {
        return
      }

      activeSelectionRef.current = event.selection

      if (!canShowToolbarRef.current || event.selection.isEmpty()) {
        showWidgetForSelection(null)
        if (event.selection.isEmpty() && !isProcessingRef.current) {
          clearProcessingHighlight()
        }
        return
      }

      // During mouse drag, selection events fire continuously.
      // Wait for mouse up before showing the Improve bubble.
      if (event.source === 'mouse' && isMouseSelectingRef.current) {
        setWidgetVisible(false)
        return
      }

      clearProcessingHighlight()
      showWidgetForSelection(event.selection)
    })

    const mouseDownDisposable = editor.onMouseDown(event => {
      if (event.event.leftButton) {
        isMouseSelectingRef.current = true
      }
    })

    const mouseUpDisposable = editor.onMouseUp(() => {
      isMouseSelectingRef.current = false
      if (isFeedbackModeRef.current) return
      if (!canShowToolbarRef.current) return

      const selection = activeSelectionRef.current
      if (!selection || selection.isEmpty()) return

      clearProcessingHighlight()
      showWidgetForSelection(selection)
    })

    return () => {
      contentDisposable.dispose()
      selectionDisposable.dispose()
      mouseDownDisposable.dispose()
      mouseUpDisposable.dispose()
      clearProcessingHighlight()
      decorationCollectionRef.current = null
      contentWidgetRef.current = null
      widgetPositionRef.current = null
      widgetVisibleRef.current = false
      isMouseSelectingRef.current = false
      setIsWidgetVisible(false)
      editor.removeContentWidget(feedbackWidget)
      setWidgetDomNode(null)
      editor.dispose()
      model.dispose()
      editorRef.current = null
      modelRef.current = null
    }
  }, [clearProcessingHighlight, isMonacoReady, monacoLanguage, showWidgetForSelection])

  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return
    editor.updateOptions({ readOnly: isReadOnly })
    if (isReadOnly) {
      if (isProcessing) {
        // Keep pending selection while processing so highlight persists for context.
        clearFeedbackState(false)
      } else {
        clearFeedbackState()
      }
    }
  }, [clearFeedbackState, isProcessing, isReadOnly])

  useEffect(() => {
    if (
      canShowToolbar &&
      !isFeedbackMode &&
      activeSelectionRef.current &&
      !activeSelectionRef.current.isEmpty()
    ) {
      showWidgetForSelection(activeSelectionRef.current)
      return
    }

    if (!canShowToolbar) {
      showWidgetForSelection(null)
    }
  }, [canShowToolbar, isFeedbackMode, showWidgetForSelection])

  useEffect(() => {
    const model = modelRef.current
    if (!model) return
    monaco.editor.setModelLanguage(model, monacoLanguage)
  }, [monacoLanguage])

  useEffect(() => {
    const model = modelRef.current
    if (!model) return
    if (model.getValue() === initialContent) return
    isApplyingExternalContentRef.current = true
    model.pushEditOperations(
      [],
      [
        {
          range: model.getFullModelRange(),
          text: initialContent
        }
      ],
      () => null
    )
    isApplyingExternalContentRef.current = false
  }, [initialContent])

  useEffect(() => {
    const wasProcessing = wasProcessingRef.current
    wasProcessingRef.current = isProcessing

    if (wasProcessing && !isProcessing) {
      clearProcessingHighlight()
    }
  }, [clearProcessingHighlight, isProcessing])

  const handleImproveClick = useCallback(() => {
    if (!activeSelectionRef.current || activeSelectionRef.current.isEmpty() || !onSelectionFeedback)
      return
    pendingSelectionRef.current = activeSelectionRef.current
    applyProcessingHighlight(activeSelectionRef.current)
    showWidgetForSelection(activeSelectionRef.current)
    setIsFeedbackMode(true)
  }, [applyProcessingHighlight, onSelectionFeedback, showWidgetForSelection])

  const handleFeedbackSubmit = useCallback(
    (feedback: string) => {
      if (!onSelectionFeedback) return

      const selection = pendingSelectionRef.current || activeSelectionRef.current
      if (!selection) return

      const context = getSelectionContext(selection)
      if (!context) return

      applyProcessingHighlight(selection)
      onSelectionFeedback(feedback, context)
      showWidgetForSelection(null)
      setIsFeedbackMode(false)
    },
    [applyProcessingHighlight, getSelectionContext, onSelectionFeedback, showWidgetForSelection]
  )

  const handleFeedbackCancel = useCallback(() => {
    clearProcessingHighlight()
    activeSelectionRef.current = null
    pendingSelectionRef.current = null
    setIsFeedbackMode(false)
    showWidgetForSelection(null)
  }, [clearProcessingHighlight, showWidgetForSelection])

  useEffect(() => {
    if (!isFeedbackMode) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return
      if (widgetDomNode?.contains(event.target)) return
      handleFeedbackCancel()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      handleFeedbackCancel()
    }

    document.addEventListener('mousedown', handlePointerDown, true)
    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [handleFeedbackCancel, isFeedbackMode, widgetDomNode])

  return (
    <div className='relative h-full bg-white'>
      {isProcessing && <div className='absolute inset-0 bg-white/60 z-10 pointer-events-auto' />}

      {widgetDomNode &&
        isWidgetVisible &&
        canShowToolbar &&
        createPortal(
          <div
            className='z-20'
            onMouseDown={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            {isFeedbackMode ? (
              <SelectionFeedbackInput
                onSubmit={handleFeedbackSubmit}
                onCancel={handleFeedbackCancel}
                placeholder='What would you like to change?'
              />
            ) : (
              <div className='flex items-center py-1 pr-1 bg-white rounded-2xl border border-tokens-neutral-border-2 shadow-popover'>
                <button
                  type='button'
                  onClick={handleImproveClick}
                  onMouseDown={e => e.preventDefault()}
                  className='flex items-center gap-2 h-9 px-4 py-2 rounded-xl bg-white hover:bg-[#F5F5F5] active:bg-[#EBEBEB] transition-colors'
                >
                  <SparkleIcon />
                  <span className='font-geist font-medium text-sm leading-[22px] text-tokens-neutral-text-primary'>
                    Improve
                  </span>
                </button>
              </div>
            )}
          </div>,
          widgetDomNode
        )}

      <div className='h-full overflow-auto pl-6 pr-8 py-4 flex flex-col items-start gap-4 flex-1 self-stretch'>
        {actionSlot && <div className='flex justify-end w-full shrink-0'>{actionSlot}</div>}

        {monacoError ? (
          <div className='w-full rounded-xl border border-tokens-status-error-30 bg-tokens-status-error-10 px-4 py-3 text-sm text-tokens-status-error-text'>
            {monacoError}
          </div>
        ) : (
          <div className='relative w-full min-h-[400px] flex-1'>
            <div ref={containerRef} className='h-full min-h-[400px] w-full' />
            {!initialContent.trim() && (
              <div className='absolute top-3 left-3 pointer-events-none text-sm text-tokens-neutral-text-placeholder font-geist'>
                {placeholder}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SparkleIcon() {
  return (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
      <path
        d='M10 2L11.8 6.2L16 8L11.8 9.8L10 14L8.2 9.8L4 8L8.2 6.2L10 2Z'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}
